import { type ToastConfig, useToast } from "@prismicio/editor-ui"
import {
  type QueryClient,
  queryOptions,
  type Updater,
  useMutation,
  useSuspenseQuery
} from "@tanstack/react-query"

import { queryClient } from "../../app/components/providers/QueryProvider"
import type { RepositoryLocale } from "../../app/services/locales"
import * as locales from "../../app/services/locales"

export interface UseRepositoryLocalesOptions {
  showToasts?: boolean
}

export type UseRepositoryLocalesResult = ReturnType<typeof useRepositoryLocales>

export function useRepositoryLocales(
  options: UseRepositoryLocalesOptions = {}
) {
  const { showToasts = false } = options

  const repositoryLocalesQuery = useSuspenseQuery(repositoryLocalesQueryOptions)

  const toast = useToast()
  const showToast = (config: ToastConfig) => {
    if (showToasts) toast.showToast(config)
  }

  const addRepositoryLocaleMutation = useMutation({
    mutationFn: locales.upsertRepositoryLocale,
    onSuccess: (insertedRepositoryLocale) => {
      setRepositoryLocalesQueryData(queryClient, (oldRepositoryLocales) =>
        addRepositoryLocale(oldRepositoryLocales, insertedRepositoryLocale)
      )
      showToast({ title: "Locale added" })
    },
    onError: () => {
      showToast({ title: "Unable to add locale", icon: "alert" })
    }
  })

  const deleteRepositoryLocaleMutation = useMutation({
    mutationFn: locales.deleteRepositoryLocale,
    onSuccess: (_response, localeId) => {
      setRepositoryLocalesQueryData(queryClient, (oldRepositoryLocales) =>
        deleteRepositoryLocale(oldRepositoryLocales, localeId)
      )
      showToast({ title: "Locale deleted" })
    },
    onError: () => {
      showToast({ title: "Unable to delete locale", icon: "alert" })
    }
  })

  const setRepositoryLocaleAsMasterMutation = useMutation({
    mutationFn: (repositoryLocale: RepositoryLocale) =>
      locales.upsertRepositoryLocale({ ...repositoryLocale, isMaster: true }),
    onSuccess: (updatedRepositoryLocale) => {
      setRepositoryLocalesQueryData(queryClient, (oldRepositoryLocales) =>
        setRepositoryLocaleAsMaster(
          oldRepositoryLocales,
          updatedRepositoryLocale.id
        )
      )
      showToast({ title: "Master locale set" })
    },
    onError: () => {
      showToast({ title: "Unable to set master locale", icon: "alert" })
    }
  })

  return {
    repositoryLocalesQuery,
    addRepositoryLocaleMutation,
    deleteRepositoryLocaleMutation,
    setRepositoryLocaleAsMasterMutation
  }
}

const repositoryLocalesQueryOptions = queryOptions({
  queryKey: ["repositoryLocales"],
  queryFn: locales.getRepositoryLocales
})

function setRepositoryLocalesQueryData(
  queryClient: QueryClient,
  updater: Updater<RepositoryLocale[], RepositoryLocale[]>
): RepositoryLocale[] | undefined {
  return queryClient.setQueryData(
    repositoryLocalesQueryOptions.queryKey,
    (oldRepositoryLocales) => {
      if (typeof updater !== "function") return updater
      if (oldRepositoryLocales === undefined) return oldRepositoryLocales
      return updater(oldRepositoryLocales)
    }
  )
}

function addRepositoryLocale(
  repositoryLocales: RepositoryLocale[],
  repositoryLocale: RepositoryLocale
): RepositoryLocale[] {
  return repositoryLocales.concat(repositoryLocale)
}

function deleteRepositoryLocale(
  repositoryLocales: RepositoryLocale[],
  localeId: RepositoryLocale["id"]
): RepositoryLocale[] {
  return repositoryLocales.filter(
    (repositoryLocale) => repositoryLocale.id !== localeId
  )
}

function setRepositoryLocaleAsMaster(
  repositoryLocales: RepositoryLocale[],
  localeId: RepositoryLocale["id"]
): RepositoryLocale[] {
  return repositoryLocales.map((repositoryLocale) => {
    if (repositoryLocale.id === localeId)
      return { ...repositoryLocale, isMaster: true }
    if (repositoryLocale.isMaster)
      return { ...repositoryLocale, isMaster: false }
    return repositoryLocale
  })
}

export function prefetchRepositoryLocalesQuery(): Promise<void> {
  return queryClient.prefetchQuery(repositoryLocalesQueryOptions)
}

export function getMasterLocale(
  repositoryLocales: RepositoryLocale[]
): RepositoryLocale | undefined {
  return repositoryLocales.find((repositoryLocale) => repositoryLocale.isMaster)
}
